import * as THREE from 'three';

/**
 * 天空：一个反面渲染的大球，用世界方向做渐变。
 * 不用 HDR 环境贴图——那会把首包撑大，而 M0 只需要「天与海的交界别露馅」。
 */
export class Sky {
  readonly mesh: THREE.Mesh;
  private readonly mat: THREE.ShaderMaterial;

  constructor(
    // 关键：用 hex 字符串而不是裸数值。three 的 ColorManagement 会把 sRGB hex 转到线性工作空间；
    // 直接写 new Color(0.6, 0.5, 0.4) 会被当成线性值，再经 colorspace_fragment 提亮一次，整体发白。
    private readonly top = new THREE.Color('#12233d'),
    private readonly horizon = new THREE.Color('#9a958c'),
    private readonly bottom = new THREE.Color('#0a1a26'),
  ) {
    const geo = new THREE.SphereGeometry(2000, 32, 24);
    this.mat = new THREE.ShaderMaterial({
      side: THREE.BackSide,
      depthWrite: false,
      fog: false,
      uniforms: {
        uTop: { value: this.top },
        uHorizon: { value: this.horizon },
        uBottom: { value: this.bottom },
        uSunDir: { value: new THREE.Vector3(0.66, 0.16, -0.73).normalize() },
        uSunColor: { value: new THREE.Color('#ffc98a') },
      },
      vertexShader: /* glsl */ `
        varying vec3 vDir;
        void main() {
          vDir = normalize(position);
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform vec3 uTop;
        uniform vec3 uHorizon;
        uniform vec3 uBottom;
        uniform vec3 uSunDir;
        uniform vec3 uSunColor;
        varying vec3 vDir;

        void main() {
          float h = vDir.y;
          vec3 col = h > 0.0
            ? mix(uHorizon, uTop, pow(clamp(h, 0.0, 1.0), 0.55))
            : mix(uHorizon, uBottom, pow(clamp(-h, 0.0, 1.0), 0.45));

          // 低悬的落日：一圈光晕 + 一个小圆盘
          float d = max(dot(normalize(vDir), normalize(uSunDir)), 0.0);
          col += uSunColor * pow(d, 220.0) * 1.6;
          col += uSunColor * pow(d, 12.0) * 0.18;
          col = mix(col, uSunColor, smoothstep(0.9993, 0.9997, d));

          gl_FragColor = vec4(col, 1.0);
          #include <colorspace_fragment>
        }
      `,
    });
    this.mesh = new THREE.Mesh(geo, this.mat);
    this.mesh.frustumCulled = false;
    this.mesh.renderOrder = -1;
  }

  /** 天空必须永远跟着相机，看起来才在无限远处。 */
  update(cameraPos: THREE.Vector3): void {
    this.mesh.position.copy(cameraPos);
  }

  /** 给海面用的太阳方向（海面高光要和天空的落日对齐）。 */
  get sunDirection(): THREE.Vector3 {
    return this.mat.uniforms.uSunDir!.value as THREE.Vector3;
  }

  get sunColor(): THREE.Color {
    return this.mat.uniforms.uSunColor!.value as THREE.Color;
  }
}
